import React, { useEffect, useRef, useCallback } from 'react';
import { theme } from '../theme';
import { useLanguage } from '../i18n/useLanguage';

export interface SidebarMenuItem {
  labelKey: string;
  onClick: () => void;
  icon?: string;
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  items: SidebarMenuItem[];
  burgerIconRef: React.RefObject<HTMLButtonElement | null>;
  footerLinks?: { label: string; onClick: () => void }[];
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, items, burgerIconRef, footerLinks }) => {
  const { t } = useLanguage();
  const panelRef = useRef<HTMLDivElement>(null);

  // Escape key handler
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // Toggle body class for content push
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('sidebar-open');
    } else {
      document.body.classList.remove('sidebar-open');
    }
    return () => document.body.classList.remove('sidebar-open');
  }, [isOpen]);

  // Focus first menu item on open
  useEffect(() => {
    if (!isOpen || !panelRef.current) return;
    const timer = setTimeout(() => {
      const firstBtn = panelRef.current?.querySelector<HTMLElement>('button');
      firstBtn?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, [isOpen]);

  // Return focus to burger icon on close
  const prevOpen = useRef(isOpen);
  useEffect(() => {
    if (prevOpen.current && !isOpen) {
      burgerIconRef.current?.focus();
    }
    prevOpen.current = isOpen;
  }, [isOpen, burgerIconRef]);

  // Focus trap
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab' || !panelRef.current) return;
    const focusable = panelRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  return (
    <>
      {/* Overlay */}
      <div
        className={`sidebar-overlay ${isOpen ? 'open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className={`sidebar-panel ${isOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={t('sidebar.close' as any)}
        onKeyDown={handleKeyDown}
      >
        <nav style={{ display: 'flex', flexDirection: 'column', padding: '80px 0 16px', flex: 1 }}>
          {/* Close button */}
          <button
            onClick={onClose}
            aria-label={t('sidebar.close' as any)}
            style={{
              position: 'absolute', top: 16, right: 16,
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 22, color: theme.muted, padding: 4,
            }}
          >
            ✕
          </button>
          {items.map((item, i) => (
            <button
              key={i}
              onClick={() => { item.onClick(); onClose(); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                minHeight: 44,
                padding: '12px 24px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: 16,
                color: theme.text,
                textAlign: 'left',
                width: '100%',
                transition: 'background 0.15s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = theme.primarySoft; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {t(item.labelKey as any)}
            </button>
          ))}
        </nav>
        {/* Footer links */}
        {footerLinks && footerLinks.length > 0 && (
          <div style={{ padding: '12px 24px 20px', borderTop: `1px solid ${theme.border}` }}>
            {footerLinks.map((link, i) => (
              <button
                key={i}
                onClick={() => { link.onClick(); onClose(); }}
                style={{
                  display: 'block', background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 13, color: theme.muted, padding: '4px 0', textAlign: 'left',
                  textDecoration: 'underline',
                }}
              >
                {link.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
};
